import { useTranslation } from 'react-i18next';
// arsenal-tpms/frontend/src/components/vehicles/detail/VehicleOverviewTab.jsx
import React from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    TextField,
    MenuItem,
    Stack,
    Divider,
    Box,
    Alert,
    useTheme,
    alpha,
    useMediaQuery
} from '@mui/material';
import VehicleSchematic from './VehicleSchematic';
import { VEHICLE_TYPES } from '../../../utils/constants';

const safeFormatDateTime = (value) => {
    try {
        if (!value) return 'N/A';
        let date;
        if (value instanceof Date) {
            date = value;
        } else if (typeof value === 'string' || typeof value === 'number') {
            date = new Date(value);
        } else {
            return 'Invalid Date';
        }
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
        return 'Error';
    }
};

const ensureString = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    return fallback;
};

const VehicleOverviewTab = ({
    currentVehicle,
    formik,
    editMode,
    vehicleConfigs,
    availableParentVehicles,
    onPositionClick,
    t
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            {/* Vehicle Information */}
            <Grid item xs={12} lg={6}>
                <Card sx={{
                    height: 'fit-content',
                    boxShadow: theme.shadows[3],
                    borderRadius: 2
                }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{
                            color: 'primary.main',
                            fontWeight: 700,
                            borderBottom: 1,
                            borderColor: 'divider',
                            pb: 1,
                            mb: 2
                        }}>
                            {t('vehicleDetail.overview.vehicleInfo', 'Información del Vehículo')}
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label={t('vehicles.form.alias', 'Alias')}
                                name={t('hardcoded.components_vehicles_detail_vehicleoverviewtab.alias')}
                                value={formik.values.alias}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.alias && Boolean(formik.errors.alias)}
                                helperText={formik.touched.alias && formik.errors.alias}
                                disabled={!editMode}
                                variant={editMode ? "outlined" : "filled"}
                                size={isMobile ? "small" : "medium"}
                            />

                            <TextField
                                fullWidth
                                label={t('vehicles.form.make', 'Marca')}
                                name="make"
                                value={formik.values.make}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.make && Boolean(formik.errors.make)}
                                helperText={formik.touched.make && formik.errors.make}
                                disabled={!editMode}
                                variant={editMode ? "outlined" : "filled"}
                                size={isMobile ? "small" : "medium"}
                            />

                            <TextField
                                fullWidth
                                label={t('vehicles.form.model', 'Modelo')}
                                name={t('hardcoded.components_vehicles_detail_vehicleoverviewtab.model')}
                                value={formik.values.model}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.model && Boolean(formik.errors.model)}
                                helperText={formik.touched.model && formik.errors.model}
                                disabled={!editMode}
                                variant={editMode ? "outlined" : "filled"}
                                size={isMobile ? "small" : "medium"}
                            />

                            <TextField
                                fullWidth
                                select
                                label={t('vehicles.form.type', 'Tipo')}
                                name="type"
                                value={formik.values.type}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.type && Boolean(formik.errors.type)}
                                helperText={formik.touched.type && formik.errors.type}
                                disabled={!editMode}
                                variant={editMode ? "outlined" : "filled"}
                                size={isMobile ? "small" : "medium"}
                            >
                                <MenuItem value="">{t('vehicles.form.selectType', 'Seleccionar Tipo')}</MenuItem>
                                {VEHICLE_TYPES.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                fullWidth
                                type="number"
                                label={t('vehicles.form.year', 'Año')}
                                name="year"
                                value={formik.values.year}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.year && Boolean(formik.errors.year)}
                                helperText={formik.touched.year && formik.errors.year}
                                disabled={!editMode}
                                variant={editMode ? "outlined" : "filled"}
                                size={isMobile ? "small" : "medium"}
                            />

                            <TextField
                                fullWidth
                                select
                                label={t('vehicles.form.configuration', 'Configuración')}
                                name="configuration_id"
                                value={formik.values.configuration_id}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.configuration_id && Boolean(formik.errors.configuration_id)}
                                helperText={formik.touched.configuration_id && formik.errors.configuration_id}
                                disabled={!editMode}
                                variant={editMode ? "outlined" : "filled"}
                                size={isMobile ? "small" : "medium"}
                            >
                                <MenuItem value="">{t('vehicles.form.selectConfiguration', 'Seleccionar Configuración')}</MenuItem>
                                {vehicleConfigs?.map((config) => (
                                    <MenuItem key={config.configurationID} value={config.configurationID}>
                                        {config.configurationP} ({config.positions} {t('vehicles.table.positions', 'posiciones')})
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                fullWidth
                                select
                                label={t('vehicles.form.parentVehicle', 'Vehículo Padre')}
                                name="parent_vehicle_id"
                                value={formik.values.parent_vehicle_id}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.parent_vehicle_id && Boolean(formik.errors.parent_vehicle_id)}
                                helperText={formik.touched.parent_vehicle_id && formik.errors.parent_vehicle_id}
                                disabled={!editMode}
                                variant={editMode ? "outlined" : "filled"}
                                size={isMobile ? "small" : "medium"}
                            >
                                <MenuItem value="">{t('vehicles.form.noParentVehicle', 'Sin Vehículo Padre')}</MenuItem>
                                {availableParentVehicles.map((v) => (
                                    <MenuItem key={v.id} value={v.id}>
                                        {v.display_name}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {/* ¿Se conduce la unidad con doble operador? */}
                            <TextField
                                fullWidth
                                select
                                label={t('hardcoded.components_vehicles_detail_vehicleoverviewtab.se_conduce_con_doble_operador')}
                                name="allow_dual_operator"
                                value={formik.values.allow_dual_operator ? 'yes' : t('common.no')}
                                onChange={(e) =>
                                    formik.setFieldValue('allow_dual_operator', e.target.value === 'yes')
                                }
                                disabled={!editMode}
                                variant={editMode ? 'outlined' : 'filled'}
                                size={isMobile ? 'small' : 'medium'}
                                helperText={t('hardcoded.components_vehicles_detail_vehicleoverviewtab.no_maximo_1_operador_si')}
                            >
                                <MenuItem value={t('common.no')}>{t('hardcoded.components_vehicles_detail_vehicleoverviewtab.no_1_operador')}</MenuItem>
                                <MenuItem value="yes">{t('hardcoded.components_vehicles_detail_vehicleoverviewtab.si_hasta_2_operadores')}</MenuItem>
                            </TextField>

                            <Divider />
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {t('vehicleDetail.overview.created', 'Creado')}
                                </Typography>
                                <Typography variant="body2">
                                    {safeFormatDateTime(currentVehicle.created_at)}
                                </Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            {/* Vehicle Schematic */}
            <Grid item xs={12} lg={6}>
                <VehicleSchematic
                    vehicle={currentVehicle}
                    t={t}
                    onPositionClick={onPositionClick}
                    editMode={editMode}
                />
                {editMode && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        {t('vehicleDetail.sensors.clickToAssign', 'Click en una posición vacía para asignar un sensor')}
                    </Alert>
                )}
            </Grid>

            {/* Connected Vehicles */}
            {currentVehicle.child_vehicles && currentVehicle.child_vehicles.length > 0 && (
                <Grid item xs={12}>
                    <Card sx={{ boxShadow: theme.shadows[3], borderRadius: 2 }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{
                                color: 'primary.main',
                                fontWeight: 700,
                                borderBottom: 1,
                                borderColor: 'divider',
                                pb: 1,
                                mb: 2
                            }}>
                                {t('vehicleDetail.overview.connectedVehicles', 'Vehículos Conectados')}
                            </Typography>
                            <Grid container spacing={2}>
                                {currentVehicle.child_vehicles.map((child) => (
                                    <Grid item xs={12} sm={6} md={4} key={child.id}>
                                        <Box sx={{
                                            p: 2,
                                            border: 2,
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            bgcolor: alpha(theme.palette.secondary.main, 0.05),
                                            '&:hover': {
                                                borderColor: 'secondary.main',
                                                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                            }
                                        }}>
                                            <Typography variant="subtitle2" fontWeight={600}>
                                                {ensureString(child.display_name)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {ensureString(child.configuration_p)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            )}
        </Grid>
    );
};

export default VehicleOverviewTab;